import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import configs from "../../utils/configs";
import { getAuthHeaders, getToken } from "../../utils/getToken";
import { store } from "../../utils/store-instance";
import { resolveDefaultWorkspace } from "./workspace-default-utils";
import { isWorkspaceSelectable as isWorkspaceSelectableByPermission } from "./workspace-selectable-utils";

const SELECTED_WORKSPACE_KEY = "selected_workspace_id";

const WorkspaceContext = createContext();

export const useWorkspaceContext = () => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error("useWorkspaceContext must be used within a WorkspaceProvider");
  }
  return context;
};

// Workspace roles constants
export const WORKSPACE_ROLES = {
  OWNER: "workspace:owner",
  CONTRIBUTOR: "workspace:contributor",
  VIEWER: "workspace:viewer"
};

export const isWorkspaceSelectable = isWorkspaceSelectableByPermission;

export const WorkspaceProvider = ({ children }) => {
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspace, setSelectedWorkspaceState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const authSnapshotRef = useRef({
    userId: null,
    lastWorkspaceId: null,
    token: null
  });

  // Workspace authorization is supplied by the ready canonical projection.
  const getWorkspaceRole = useCallback(workspace => {
    if (workspace?.authorization?.projection_status !== "ready") return null;
    return workspace.authorization.role || null;
  }, []);

  const getDefaultWorkspace = useCallback(() => {
    const authUser = store?.state?.authUser;
    return resolveDefaultWorkspace({ workspaces, authUser, selectedWorkspace });
  }, [workspaces, selectedWorkspace]);



  const setSelectedWorkspace = useCallback(workspace => {
    setSelectedWorkspaceState(workspace);
    if (workspace) {
      localStorage.setItem(SELECTED_WORKSPACE_KEY, workspace.id);
    } else {
      localStorage.removeItem(SELECTED_WORKSPACE_KEY);
    }
  }, []);

  const fetchWorkspaces = useCallback(
    async (token, preSelectedId = null, lastWorkspaceId = null, retryCount = 0) => {
      if (!token) {
        setError("No token available");
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const url = `${configs.CUSTOM_API_URL}/workspaces`;
        const response = await fetch(url, {
          headers: {
            ...(await getAuthHeaders(url, "GET"))
          }
        });

        if (!response.ok) {
          if (retryCount < 2) {
            console.warn(`Failed to fetch workspaces, retrying (${retryCount + 1}/2)...`);
            await new Promise(resolve => setTimeout(resolve, 1000));
            return fetchWorkspaces(token, preSelectedId, lastWorkspaceId, retryCount + 1);
          }
          throw new Error(`Failed to fetch workspaces: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        const workspacesList = Array.isArray(data) ? data : data.data || [];

        if (!Array.isArray(workspacesList) || workspacesList.length === 0) {
          console.warn("No workspaces found for user");
          // If no workspaces found, inject a dummy default workspace
          const dummyWorkspace = {
            id: "default",
            name: "Default Workspace",
            is_default: true,
            type: "private"
          };
          setWorkspaces([dummyWorkspace]);
          setSelectedWorkspace(dummyWorkspace);
          setLoading(false);
          return;
        }

        setWorkspaces(workspacesList);

        // Handle selection logic...
        let workspaceToSelect = null;
        if (preSelectedId) {
          const candidate = workspacesList.find(w => w.id === preSelectedId);
          if (isWorkspaceSelectable(candidate)) {
            workspaceToSelect = candidate;
          }
        }

        if (!workspaceToSelect) {
          const savedId = localStorage.getItem(SELECTED_WORKSPACE_KEY);
          if (savedId) {
            const candidate = workspacesList.find(w => w.id === savedId);
            if (isWorkspaceSelectable(candidate)) {
              workspaceToSelect = candidate;
            }
          }
        }

        if (!workspaceToSelect && lastWorkspaceId) {
          const candidate = workspacesList.find(w => w.id === lastWorkspaceId);
          if (isWorkspaceSelectable(candidate)) {
            workspaceToSelect = candidate;
          }
        }

        if (!workspaceToSelect) {
          // Prefer default workspace that is selectable
          workspaceToSelect =
            workspacesList.find(w => w.is_default && isWorkspaceSelectable(w)) ||
            workspacesList.find(isWorkspaceSelectable) ||
            workspacesList[0];
        }

        if (workspaceToSelect) {
          setSelectedWorkspace(workspaceToSelect);
        } else if (workspacesList[0]) {
          // Final fallback if for some reason none are selectable
          setSelectedWorkspace(workspacesList[0]);
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching workspaces:", error);
        setError(error.message);
        if (retryCount < 2) {
          console.warn(`Error fetching workspaces, retrying (${retryCount + 1}/2)...`);
          await new Promise(resolve => setTimeout(resolve, 1000));
          return fetchWorkspaces(token, preSelectedId, lastWorkspaceId, retryCount + 1);
        }

        // Final fallback on error
        const dummyWorkspace = {
          id: "default-error",
          name: "Default Workspace (Offline)",
          is_default: true,
          type: "private"
        };
        setWorkspaces([dummyWorkspace]);
        setSelectedWorkspace(dummyWorkspace);
        setLoading(false);
      }
    },
    [setSelectedWorkspace]
  );

  // Compute workspace role based on current user and selected workspace
  const workspaceRole = useMemo(() => {
    return getWorkspaceRole(selectedWorkspace);
  }, [selectedWorkspace, getWorkspaceRole]);

  // Auto-fetch workspaces when user is authenticated
  useEffect(() => {
    const getAuthSnapshot = () => {
      const authUser = store?.state?.authUser;
      const token = store?.state?.credentials?.token || null;

      return {
        userId: authUser?.id || null,
        lastWorkspaceId: authUser?.last_workspace_id || null,
        token
      };
    };

    const hasSnapshotChanged = nextSnapshot => {
      const previousSnapshot = authSnapshotRef.current;
      return (
        nextSnapshot.userId !== previousSnapshot.userId ||
        nextSnapshot.lastWorkspaceId !== previousSnapshot.lastWorkspaceId ||
        nextSnapshot.token !== previousSnapshot.token
      );
    };

    const loadWorkspacesIfNeeded = async (force = false) => {
      const nextSnapshot = getAuthSnapshot();
      if (!force && !hasSnapshotChanged(nextSnapshot)) return;
      authSnapshotRef.current = nextSnapshot;

      if (!nextSnapshot.userId) {
        // Clear workspaces if user is not authenticated
        setWorkspaces([]);
        setSelectedWorkspaceState(null);
        return;
      }

      const token = await getToken();
      if (token) {
        await fetchWorkspaces(token, null, nextSnapshot.lastWorkspaceId);
      }
    };

    // Initial load
    loadWorkspacesIfNeeded(true);

    // Listen for authentication changes via store events
    if (store && store.addEventListener) {
      const handleStoreChange = () => {
        loadWorkspacesIfNeeded();
      };

      // Listen for state changes (if the store supports it)
      store.addEventListener("statechanged", handleStoreChange);
      store.addEventListener("authchanged", handleStoreChange);

      return () => {
        if (store.removeEventListener) {
          store.removeEventListener("statechanged", handleStoreChange);
          store.removeEventListener("authchanged", handleStoreChange);
        }
      };
    }
  }, [fetchWorkspaces]);

  const contextValue = useMemo(
    () => ({
      workspaces,
      selectedWorkspace,
      setSelectedWorkspace,
      loading,
      error,
      fetchWorkspaces,
      workspaceRole,
      getWorkspaceRole,
      getDefaultWorkspace
    }),
    [
      workspaces,
      selectedWorkspace,
      setSelectedWorkspace,
      loading,
      error,
      fetchWorkspaces,
      workspaceRole,
      getWorkspaceRole,
      getDefaultWorkspace
    ]
  );

  return <WorkspaceContext.Provider value={contextValue}>{children}</WorkspaceContext.Provider>;
};

WorkspaceProvider.propTypes = {
  children: PropTypes.node.isRequired
};
